import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';

/**
 * AssessmentNotesTab
 *
 * Handles Assessment Notes section for MH triage assessments
 * Maps to TriageDetail and NPDetails table fields
 */

/**
 * Convert DateOnly object to YYYY-MM-DD string format
 * Handles both uppercase (Year, Month, Day) and lowercase (year, month, day) properties
 * @param {object} dateOnly - DateOnly object
 * @returns {string} Date string in YYYY-MM-DD format, or empty string if null
 */
const dateOnlyToString = (dateOnly) => {
  if (!dateOnly) return '';
  // Handle both uppercase and lowercase property names
  const year = dateOnly.year || dateOnly.Year;
  const month = dateOnly.month || dateOnly.Month;
  const day = dateOnly.day || dateOnly.Day;
  if (!year) return '';
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Convert YYYY-MM-DD string to DateOnly object {year, month, day}
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {object|null} DateOnly object or null if empty/invalid
 */
const stringToDateOnly = (dateString) => {
  if (!dateString) return null;
  const parts = dateString.split('-');
  if (parts.length !== 3) return null;
  return {
    year: parseInt(parts[0], 10),
    month: parseInt(parts[1], 10),
    day: parseInt(parts[2], 10)
  };
};

const AssessmentNotesTab = ({ reportId, api, patientInfo, onSave , isPublished}) => {
  // Form state
  const [problemBriefDescription, setProblemBriefDescription] = useState('');
  const [problemDevelopmentMaintenance, setProblemDevelopmentMaintenance] = useState('');
  const [currentEpisodeOnsetDate, setCurrentEpisodeOnsetDate] = useState('');
  const [pastPsychProblemsDescription, setPastPsychProblemsDescription] = useState('');
  const [medicalHistoryDescription, setMedicalHistoryDescription] = useState('');
  const [currentPrescribedMedication, setCurrentPrescribedMedication] = useState('');
  const [disorderSpecific, setDisorderSpecific] = useState('');
  const [additionalSubstanceUse, setAdditionalSubstanceUse] = useState('');
  const [workRelated, setWorkRelated] = useState(null);
  const [incidentReportingFormCompleted, setIncidentReportingFormCompleted] = useState(null);
  const [previousAdminssions, setPreviousAdminssions] = useState('');
  const [previousSecondaryCare, setPreviousSecondaryCare] = useState('');
  const [isChronicCondition, setIsChronicCondition] = useState(null);
  const [chronicConditionStartDate, setChronicConditionStartDate] = useState('');
  const [doesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist, setDoesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});

  useEffect(() => {
    loadAssessmentNotesData();
  }, [reportId]);

  const loadAssessmentNotesData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-assessment-notes`);

      if (response.ok) {
        const data = await response.json();
        setProblemBriefDescription(data.problemBriefDescription || '');
        setProblemDevelopmentMaintenance(data.problemDevelopmentMaintenance || '');
        // CurrentEpisodeOnsetDate comes as a date string "YYYY-MM-DD"
        setCurrentEpisodeOnsetDate(data.currentEpisodeOnsetDate || '');
        setPastPsychProblemsDescription(data.pastPsychProblemsDescription || '');
        setMedicalHistoryDescription(data.medicalHistoryDescription || '');
        setCurrentPrescribedMedication(data.currentPrescribedMedication || '');
        setDisorderSpecific(data.disorderSpecific || '');
        setAdditionalSubstanceUse(data.additionalSubstanceUse || '');
        setWorkRelated(data.workRelated);
        setIncidentReportingFormCompleted(data.incidentReportingFormCompleted);
        setPreviousAdminssions(data.previousAdminssions || '');
        setPreviousSecondaryCare(data.previousSecondaryCare || '');
        setIsChronicCondition(data.isChronicCondition);
        // ChronicConditionStartDate comes as date-time string, extract YYYY-MM-DD
        setChronicConditionStartDate(data.chronicConditionStartDate ? data.chronicConditionStartDate.split('T')[0] : '');
        setDoesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist(data.doesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist || '');

        // Set loaded fields as 'saved' to show green indicator
        const initialSavingStates = {};
        if (data.problemBriefDescription) initialSavingStates['ProblemBriefDescription'] = 'saved';
        if (data.problemDevelopmentMaintenance) initialSavingStates['ProblemDevelopmentMaintenance'] = 'saved';
        if (data.currentEpisodeOnsetDate) initialSavingStates['CurrentEpisodeOnsetDate'] = 'saved';
        if (data.pastPsychProblemsDescription) initialSavingStates['PastPsychProblemsDescription'] = 'saved';
        if (data.medicalHistoryDescription) initialSavingStates['MedicalHistoryDescription'] = 'saved';
        if (data.currentPrescribedMedication) initialSavingStates['CurrentPrescribedMedication'] = 'saved';
        if (data.disorderSpecific) initialSavingStates['DisorderSpecific'] = 'saved';
        if (data.additionalSubstanceUse) initialSavingStates['AdditionalSubstanceUse'] = 'saved';
        if (data.workRelated !== null) initialSavingStates['WorkRelated'] = 'saved';
        if (data.incidentReportingFormCompleted !== null) initialSavingStates['IncidentReportingFormCompleted'] = 'saved';
        if (data.previousAdminssions) initialSavingStates['PreviousAdminssions'] = 'saved';
        if (data.previousSecondaryCare) initialSavingStates['PreviousSecondaryCare'] = 'saved';
        if (data.isChronicCondition !== null) initialSavingStates['IsChronicCondition'] = 'saved';
        if (data.chronicConditionStartDate) initialSavingStates['ChronicConditionStartDate'] = 'saved';
        if (data.doesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist) initialSavingStates['DoesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist'] = 'saved';
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading assessment notes data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-assessment-notes/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify(value)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    // Field value map for checking if field has content
    const fieldValues = {
      'ProblemBriefDescription': problemBriefDescription,
      'ProblemDevelopmentMaintenance': problemDevelopmentMaintenance,
      'CurrentEpisodeOnsetDate': currentEpisodeOnsetDate,
      'PastPsychProblemsDescription': pastPsychProblemsDescription,
      'MedicalHistoryDescription': medicalHistoryDescription,
      'CurrentPrescribedMedication': currentPrescribedMedication,
      'DisorderSpecific': disorderSpecific,
      'AdditionalSubstanceUse': additionalSubstanceUse,
      'WorkRelated': workRelated,
      'IncidentReportingFormCompleted': incidentReportingFormCompleted,
      'PreviousAdminssions': previousAdminssions,
      'PreviousSecondaryCare': previousSecondaryCare,
      'IsChronicCondition': isChronicCondition,
      'ChronicConditionStartDate': chronicConditionStartDate,
      'DoesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist': doesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const getCharacterCounter = (currentLength, maxLength) => {
    if (!maxLength) return null;
    const remaining = maxLength - currentLength;
    const isNearLimit = remaining <= 50;
    return (
      <span className={`character-counter ${isNearLimit ? 'near-limit' : ''}`}>
        {currentLength} / {maxLength} characters
      </span>
    );
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    // Track initial character count if not already tracking
    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    // Calculate characters typed since start
    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    // Clear existing timeout
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Trigger save if 50+ characters typed
    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    // Debounce save - 10 seconds after typing stops
    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000);

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    // Clear debounce timeout on blur
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Reset character count for next typing session
    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));

    // Save immediately on blur
    saveField(fieldName, value);
  };

  const handleBooleanChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  const handleDateChange = (fieldName, value, setter) => {
    setter(value);

    // ChronicConditionStartDate expects date-time string format
    if (fieldName === 'ChronicConditionStartDate') {
      // Convert YYYY-MM-DD to ISO date-time string
      const dateTimeValue = value ? `${value}T00:00:00` : null;
      saveField(fieldName, dateTimeValue);
    } else {
      // Other date fields (like CurrentEpisodeOnsetDate) expect simple date string YYYY-MM-DD
      saveField(fieldName, value || null);
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading assessment notes...</p>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="patient-summary">
        <div><strong>Case ID:</strong> {patientInfo?.caseId}</div>
        <div><strong>Name:</strong> {patientInfo?.name}</div>
        <div><strong>DOB:</strong> {patientInfo?.dateOfBirth}</div>
        <div><strong>Address:</strong> {patientInfo?.address}</div>
      </div>

      <div className="form-section">
        <h3>Problem Description</h3>

        <div className="form-group">
          <label htmlFor="problemBriefDescription">
            Brief description of problem:
            {getSaveIndicator('ProblemBriefDescription')}
          </label>
          <textarea
            id="problemBriefDescription"
            value={problemBriefDescription}
            onChange={(e) => handleFieldChange('ProblemBriefDescription', e.target.value, setProblemBriefDescription)}
            onBlur={() => handleFieldBlur('ProblemBriefDescription', problemBriefDescription)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="problemDevelopmentMaintenance">
            Development and maintenance of problem:
            {getSaveIndicator('ProblemDevelopmentMaintenance')}
          </label>
          <textarea
            id="problemDevelopmentMaintenance"
            value={problemDevelopmentMaintenance}
            onChange={(e) => handleFieldChange('ProblemDevelopmentMaintenance', e.target.value, setProblemDevelopmentMaintenance)}
            onBlur={() => handleFieldBlur('ProblemDevelopmentMaintenance', problemDevelopmentMaintenance)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="currentEpisodeOnsetDate">
            Date of onset of current episode:
            {getSaveIndicator('CurrentEpisodeOnsetDate')}
          </label>
          <input
            type="date"
            id="currentEpisodeOnsetDate"
            value={currentEpisodeOnsetDate}
            onChange={(e) => handleDateChange('CurrentEpisodeOnsetDate', e.target.value, setCurrentEpisodeOnsetDate)}
            style={{ width: '250px' }}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Medical History</h3>

        <div className="form-group">
          <label htmlFor="pastPsychProblemsDescription">
            Brief description of past psychological problems including treatments:
            {getSaveIndicator('PastPsychProblemsDescription')}
          </label>
          <textarea
            id="pastPsychProblemsDescription"
            value={pastPsychProblemsDescription}
            onChange={(e) => handleFieldChange('PastPsychProblemsDescription', e.target.value, setPastPsychProblemsDescription)}
            onBlur={() => handleFieldBlur('PastPsychProblemsDescription', pastPsychProblemsDescription)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="medicalHistoryDescription">
            Brief description of any relevant medical history or diagnosis including treatments:
            {getSaveIndicator('MedicalHistoryDescription')}
          </label>
          <textarea
            id="medicalHistoryDescription"
            value={medicalHistoryDescription}
            onChange={(e) => handleFieldChange('MedicalHistoryDescription', e.target.value, setMedicalHistoryDescription)}
            onBlur={() => handleFieldBlur('MedicalHistoryDescription', medicalHistoryDescription)}
            rows="4"
            maxLength="1000"
            style={{ width: '100%' }}
          />
          {getCharacterCounter(medicalHistoryDescription.length, 1000)}
        </div>

        <div className="form-group">
          <label htmlFor="currentPrescribedMedication">
            Current prescribed medication (e.g., psychotropic):
            {getSaveIndicator('CurrentPrescribedMedication')}
          </label>
          <textarea
            id="currentPrescribedMedication"
            value={currentPrescribedMedication}
            onChange={(e) => handleFieldChange('CurrentPrescribedMedication', e.target.value, setCurrentPrescribedMedication)}
            onBlur={() => handleFieldBlur('CurrentPrescribedMedication', currentPrescribedMedication)}
            rows="3"
            maxLength="1000"
            style={{ width: '100%' }}
          />
          {getCharacterCounter(currentPrescribedMedication.length, 1000)}
        </div>

        <div className="form-group">
          <label htmlFor="previousAdminssions">
            Previous admissions to psychiatric or psychological care:
            {getSaveIndicator('PreviousAdminssions')}
          </label>
          <textarea
            id="previousAdminssions"
            value={previousAdminssions}
            onChange={(e) => handleFieldChange('PreviousAdminssions', e.target.value, setPreviousAdminssions)}
            onBlur={() => handleFieldBlur('PreviousAdminssions', previousAdminssions)}
            rows="3"
            maxLength="1000"
            style={{ width: '100%' }}
          />
          {getCharacterCounter(previousAdminssions.length, 1000)}
        </div>

        <div className="form-group">
          <label htmlFor="previousSecondaryCare">
            Previous secondary care:
            {getSaveIndicator('PreviousSecondaryCare')}
          </label>
          <textarea
            id="previousSecondaryCare"
            value={previousSecondaryCare}
            onChange={(e) => handleFieldChange('PreviousSecondaryCare', e.target.value, setPreviousSecondaryCare)}
            onBlur={() => handleFieldBlur('PreviousSecondaryCare', previousSecondaryCare)}
            rows="3"
            maxLength="1000"
            style={{ width: '100%' }}
          />
          {getCharacterCounter(previousSecondaryCare.length, 1000)}
        </div>

        <div className="form-group">
          <label className="inline-label">
            <strong>Is this a chronic condition?</strong>
            {getSaveIndicator('IsChronicCondition')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="isChronicCondition"
                checked={isChronicCondition === true}
                onChange={() => handleBooleanChange('IsChronicCondition', true, setIsChronicCondition)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="isChronicCondition"
                checked={isChronicCondition === false}
                onChange={() => handleBooleanChange('IsChronicCondition', false, setIsChronicCondition)}
              />
              No
            </label>
          </div>
        </div>

        {isChronicCondition === true && (
          <div className="form-group">
            <label htmlFor="chronicConditionStartDate">
              If so, when did the chronic condition start?
              {getSaveIndicator('ChronicConditionStartDate')}
            </label>
            <input
              type="date"
              id="chronicConditionStartDate"
              value={chronicConditionStartDate}
              onChange={(e) => handleDateChange('ChronicConditionStartDate', e.target.value, setChronicConditionStartDate)}
              style={{ width: '250px' }}
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="doesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist">
            Does the patient have any formal MH diagnosis from a psychiatrist?
            {getSaveIndicator('DoesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist')}
          </label>
          <textarea
            id="doesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist"
            value={doesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist}
            onChange={(e) => handleFieldChange('DoesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist', e.target.value, setDoesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist)}
            onBlur={() => handleFieldBlur('DoesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist', doesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist)}
            rows="3"
            maxLength="1000"
            style={{ width: '100%' }}
          />
          {getCharacterCounter(doesThePatientHaveAnyFormalMhDiagnosisFromAPsychiatrist.length, 1000)}
        </div>
      </div>

      <div className="form-section">
        <h3>Additional Information</h3>

        <div className="form-group">
          <label htmlFor="disorderSpecific">
            Disorder specific:
            {getSaveIndicator('DisorderSpecific')}
          </label>
          <textarea
            id="disorderSpecific"
            value={disorderSpecific}
            onChange={(e) => handleFieldChange('DisorderSpecific', e.target.value, setDisorderSpecific)}
            onBlur={() => handleFieldBlur('DisorderSpecific', disorderSpecific)}
            rows="3"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="additionalSubstanceUse">
            Additional substance use (include alcohol, herbal and illicit drugs):
            {getSaveIndicator('AdditionalSubstanceUse')}
          </label>
          <textarea
            id="additionalSubstanceUse"
            value={additionalSubstanceUse}
            onChange={(e) => handleFieldChange('AdditionalSubstanceUse', e.target.value, setAdditionalSubstanceUse)}
            onBlur={() => handleFieldBlur('AdditionalSubstanceUse', additionalSubstanceUse)}
            rows="3"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label className="inline-label">
            <strong>Is the problem work-related?</strong>
            {getSaveIndicator('WorkRelated')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="workRelated"
                checked={workRelated === true}
                onChange={() => handleBooleanChange('WorkRelated', true, setWorkRelated)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="workRelated"
                checked={workRelated === false}
                onChange={() => handleBooleanChange('WorkRelated', false, setWorkRelated)}
              />
              No
            </label>
          </div>
        </div>

        {workRelated === true && (
          <div className="form-group">
            <label className="inline-label">
              <strong>If so, have you completed an incident reporting form?</strong>
              {getSaveIndicator('IncidentReportingFormCompleted')}
            </label>
            <div className="radio-group-inline">
              <label>
                <input
                  type="radio"
                  name="incidentReportingFormCompleted"
                  checked={incidentReportingFormCompleted === true}
                  onChange={() => handleBooleanChange('IncidentReportingFormCompleted', true, setIncidentReportingFormCompleted)}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="incidentReportingFormCompleted"
                  checked={incidentReportingFormCompleted === false}
                  onChange={() => handleBooleanChange('IncidentReportingFormCompleted', false, setIncidentReportingFormCompleted)}
                />
                No
              </label>
            </div>
          </div>
        )}
      </div>
      </fieldset>

    </div>
  );
};

export default AssessmentNotesTab;
